import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  ArrowUpRight,
  Building2,
  Users,
  Factory,
  Ship,
  CheckCircle2,
  Network,
  Landmark,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";
const clientImage = "/assets/images/bg2.jpeg";

const majorClients = [
  {
    name: "Chevron Nigeria",
    category: "Oil Servicing Company",
    location: "Nigeria",
  },
  {
    name: "Intels",
    category: "Oil Servicing & Logistics Company",
    location: "Port Harcourt / Onne",
  },
  {
    name: "Addax Petroleum",
    category: "Oil Servicing Company",
    location: "Lagos",
  },
  {
    name: "Halliburton Engineering",
    category: "Oil Servicing Company",
    location: "Port Harcourt",
  },
  {
    name: "Agip Oil",
    category: "Oil Servicing Company",
    location: "Port Harcourt",
  },
  {
    name: "Exxon Mobil Nigeria",
    category: "Oil Exploration Company",
    location: "Ibeno",
  },
];

const ecosystem = [
  {
    title: "Oil & Gas Operations",
    text:
      "Clients operate across upstream, midstream and downstream oil and gas value chains.",
    icon: Factory,
  },
  {
    title: "Marine & Logistics",
    text:
      "The zones support cargo movement, marine services, warehousing and offshore project logistics.",
    icon: Ship,
  },
  {
    title: "Strategic Partnerships",
    text:
      "OGFZA’s client ecosystem is strengthened through private sector participation and institutional support.",
    icon: Network,
  },
];

const advantages = [
  "High-quality services that complement free zone operations.",
  "Strategic presence across oil servicing, logistics, engineering and exploration.",
  "Strong contribution to investment growth and industrial development.",
  "Support for Nigeria’s position as a regional oil and gas logistics hub.",
];

const ClientOverview = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 50,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".client-row").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          x: -35,
          delay: index * 0.04,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 92%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="Client Overview"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/75" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/85 via-[#082567]/55 to-[#026B07]/25" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/35 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/15 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Enterprise Ecosystem
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Client Overview
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            OGFZA highlights strategic partners and clients whose operations
            complement the Authority’s free zone ecosystem and strengthen
            Nigeria’s oil and gas investment environment.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Client Overview</span>
          </div>
        </div>
      </section>

      {/* INTRO WITH SIDE IMAGE */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Client Overview Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
        />
        <div className="absolute inset-0 bg-white/95" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-14 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Strategic Clients
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Trusted Enterprises Supporting The Free Zone Economy
            </h2>

            <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

            <p className="text-slate-700 leading-[1.9] text-[15.5px] mb-5">
              OGFZA highlights strategic partners and clients that provide
              high-quality services and complement operations across Nigeria’s
              Oil and Gas Free Zones.
            </p>

            <p className="text-slate-700 leading-[1.9] text-[15.5px]">
              These companies operate across oil servicing, logistics,
              engineering, exploration and industrial support services,
              strengthening the Authority’s role as a gateway for investment and
              energy sector growth.
            </p>
          </div>

          <div className="reveal-up relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#F2AF11]/15 blur-2xl" />
            <div className="absolute -bottom-8 -right-8 w-36 h-36 rounded-full bg-[#026B07]/12 blur-2xl" />

            <div className="relative rounded-[34px] overflow-hidden border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.5)] bg-white p-3">
              <img
                src={clientImage}
                alt="OGFZA Clients"
                className="w-full h-[520px] object-cover rounded-[26px]"
              />

              <div className="absolute inset-3 rounded-[26px] bg-gradient-to-t from-[#082567]/80 via-transparent to-transparent" />

              <div className="absolute bottom-7 left-7 right-7">
                <div className="bg-white/95 backdrop-blur-md rounded-[24px] p-5 border border-white shadow-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                      <Users size={24} />
                    </div>

                    <div>
                      <p className="text-[#026B07] font-black tracking-[0.18em] uppercase text-[11px] mb-2">
                        Client Network
                      </p>

                      <h3
                        className="text-xl font-black text-[#082567] leading-tight"
                        style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                      >
                        Oil servicing, logistics, engineering and exploration
                        companies.
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CLIENT DIRECTORY */}
      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div>
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Listed Clients
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight max-w-3xl"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Key Companies Within The OGFZA Ecosystem
              </h2>
            </div>

            <div className="max-w-md border-l-4 border-[#F2AF11] pl-6">
              <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                These clients reflect the range of operators and strategic
                partners connected to Nigeria’s oil and gas free zone economy.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-[32px] border border-slate-200 overflow-hidden shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
            <div className="hidden md:grid grid-cols-[1.2fr_1.2fr_0.8fr] gap-6 px-7 py-5 bg-[#082567] text-white">
              <p className="font-black text-sm">Company</p>
              <p className="font-black text-sm">Service Category</p>
              <p className="font-black text-sm">Location</p>
            </div>

            {majorClients.map((client, index) => (
              <div
                key={index}
                className="client-row grid md:grid-cols-[1.2fr_1.2fr_0.8fr] gap-5 px-7 py-6 border-b border-slate-200 last:border-b-0 hover:bg-[#FAFBFC] transition-colors duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                    <Building2 size={21} />
                  </div>

                  <div>
                    <p className="md:hidden text-[#026B07] font-black tracking-[0.15em] uppercase text-[11px] mb-1">
                      Company
                    </p>

                    <h3 className="text-[#082567] font-black text-[16px]">
                      {client.name}
                    </h3>
                  </div>
                </div>

                <div>
                  <p className="md:hidden text-[#026B07] font-black tracking-[0.15em] uppercase text-[11px] mb-1">
                    Category
                  </p>

                  <p className="text-slate-700 leading-[1.7] text-[15px]">
                    {client.category}
                  </p>
                </div>

                <div>
                  <p className="md:hidden text-[#026B07] font-black tracking-[0.15em] uppercase text-[11px] mb-1">
                    Location
                  </p>

                  <p className="text-slate-600 text-[15px]">
                    {client.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM WITH SIDE IMAGE */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Client Ecosystem Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
        />
        <div className="absolute inset-0 bg-white/95" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-center">
          <div className="reveal-up relative order-2 lg:order-1">
            <div className="relative rounded-[34px] overflow-hidden border border-slate-200 bg-white p-3 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
              <img
                src={sectionBg}
                alt="OGFZA Client Ecosystem"
                className="w-full h-[480px] object-cover rounded-[26px]"
              />

              <div className="absolute inset-3 rounded-[26px] bg-gradient-to-t from-[#082567]/80 via-[#082567]/15 to-transparent" />

              <div className="absolute bottom-7 left-7 right-7">
                <div className="bg-white rounded-[24px] p-5 shadow-xl">
                  <p className="text-[#026B07] font-black tracking-[0.18em] uppercase text-[11px] mb-2">
                    Client Ecosystem
                  </p>

                  <h3
                    className="text-xl font-black text-[#082567] leading-tight"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    Energy, logistics and industrial support working together.
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="reveal-up mb-8">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Client Ecosystem
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-6"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                A Network Built Around Energy, Logistics & Industrial Support
              </h2>

              <div className="w-24 h-1.5 bg-[#F2AF11] rounded-full mb-7" />

              <p className="text-slate-600 leading-[1.9] text-[15.5px]">
                OGFZA’s client base supports the free zone model by delivering
                services that help operators move goods, support projects,
                maintain infrastructure and serve regional energy markets.
              </p>
            </div>

            <div className="grid gap-5">
              {ecosystem.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="reveal-up bg-white border border-slate-200 rounded-[26px] p-6 hover:-translate-y-2 hover:border-[#F2AF11]/60 hover:shadow-[0_24px_80px_-58px_rgba(8,37,103,0.55)] transition-all duration-500"
                  >
                    <div className="flex flex-col sm:flex-row gap-5">
                      <div className="w-13 h-13 rounded-2xl bg-[#F2AF11]/15 text-[#082567] border border-[#F2AF11]/25 flex items-center justify-center flex-shrink-0">
                        <Icon size={24} />
                      </div>

                      <div>
                        <h3
                          className="text-2xl font-black text-[#082567] leading-tight mb-3"
                          style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                        >
                          {item.title}
                        </h3>

                        <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up bg-white rounded-[36px] border border-slate-200 p-8 md:p-12 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10">
              <div>
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#FAFBFC] border border-slate-200 mb-7">
                  <Landmark size={18} className="text-[#F2AF11]" />
                  <span className="text-[#082567] font-black text-sm">
                    Strategic Value
                  </span>
                </div>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Clients strengthen the operational depth of Nigeria’s Oil and
                  Gas Free Zones.
                </h2>
              </div>

              <div className="space-y-4">
                {advantages.map((item, index) => (
                  <div
                    key={index}
                    className="flex gap-4 border-b border-slate-200 last:border-b-0 pb-4 last:pb-0"
                  >
                    <CheckCircle2
                      size={20}
                      className="text-[#026B07] flex-shrink-0 mt-1"
                    />

                    <p className="text-slate-700 leading-[1.8] text-[15.5px]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLEAN CTA */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Free Zones Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
        />
        <div className="absolute inset-0 bg-white/95" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border-t border-slate-200 pt-14">
            <div>
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Explore More
              </p>

              <h2
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight max-w-3xl"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Discover the free zones where these strategic enterprises
                operate.
              </h2>
            </div>

            <Link
              to="/free-zones/onne"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500 whitespace-nowrap"
            >
              Explore Free Zones
              <ArrowUpRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ClientOverview;